<template>
  <div class="size-guide-page">
    <!-- Dark Visualizer Section -->
    <div class="sg-dark">
      <div class="sg-dark-grid" />
      <div class="sg-dark-inner">
        <!-- Eyebrow -->
        <div class="sg-eyebrow reveal">
          <span>Size Guide</span>
        </div>

        <!-- Title -->
        <h1 class="sg-title reveal reveal-delay-1">{{ heading }}</h1>
        <p class="sg-subtitle reveal reveal-delay-2">
          Understand your sunglasses size
        </p>

        <!-- Size Picker -->
        <div class="sg-picker reveal reveal-delay-3">
          <button
            v-for="s in sizeKeys"
            :key="s"
            :class="['sg-picker-btn', { active: size === s }]"
            @click="size = s"
          >
            {{ s }}
          </button>
        </div>

        <!-- Hint (desktop only) -->
        <p class="sg-hint">
          Hover over annotations · Pick a size to see it scale
        </p>

        <!-- Front view -->
        <div class="sg-front-wrap reveal">
          <GlassesFront
            :vals="animatedVals"
            :active-label="hovered"
            @label-hover="hovered = $event"
          />
        </div>

        <div class="sg-divider" />

        <!-- Side view -->
        <div class="sg-side-wrap reveal">
          <GlassesSide
            :vals="animatedVals"
            :active-label="hovered"
            @label-hover="hovered = $event"
          />
        </div>

        <div class="sg-divider-2" />

        <!-- Number readout -->
        <div class="sg-readout reveal">
          <div class="sg-readout-label">Frame Size</div>

          <div class="sg-big-numbers">
            <div class="sg-big-num">
              <div class="sg-big-num-val">{{ r(animatedVals.lensW) }}</div>
            </div>
            <div class="sg-big-sep sg-big-sep--box">□</div>
            <div class="sg-big-num">
              <div class="sg-big-num-val">{{ r(animatedVals.bridge) }}</div>
            </div>
            <div class="sg-big-sep sg-big-sep--dash">—</div>
            <div class="sg-big-num">
              <div class="sg-big-num-val">{{ r(animatedVals.temple) }}</div>
            </div>
          </div>

          <!-- Sub-labels -->
          <div class="sg-sub-labels">
            <div class="sg-sub-label">
              <div class="sg-sub-label-title">Lens Width</div>
              <div class="sg-sub-label-desc">Horizontal width of one lens</div>
            </div>
            <div class="sg-sub-label">
              <div class="sg-sub-label-title">Bridge Width</div>
              <div class="sg-sub-label-desc">Width between lenses</div>
            </div>
            <div class="sg-sub-label">
              <div class="sg-sub-label-title">Temple Length</div>
              <div class="sg-sub-label-desc">Hinge to tip</div>
            </div>
          </div>

          <!-- Size badge -->
          <div class="sg-badge">
            <span class="sg-badge-size">{{ size }}</span>
            <span class="sg-badge-divider" />
            <span class="sg-badge-desc">{{ currentSize.desc }}</span>
            <span class="sg-badge-best">{{ currentSize.best }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Warm Section -->
    <div class="sg-warm">
      <div class="sg-warm-inner">
        <!-- Number breakdown -->
        <div class="sg-breakdown reveal">
          <div class="sg-breakdown-left">
            <div class="sg-breakdown-title">Frame Size</div>
            <div class="sg-breakdown-nums">
              <span class="sg-breakdown-num">{{ r(animatedVals.lensW) }}</span>
              <span class="sg-breakdown-sep">□</span>
              <span class="sg-breakdown-num">{{ r(animatedVals.bridge) }}</span>
              <span class="sg-breakdown-sep sg-breakdown-sep--dash">—</span>
              <span class="sg-breakdown-num">{{ r(animatedVals.temple) }}</span>
            </div>
            <div class="sg-breakdown-hint">
              The numbers on the inside of the temple show the frame size in
              millimeters.
            </div>
          </div>

          <div class="sg-breakdown-grid">
            <div class="sg-breakdown-col">
              <div class="sg-breakdown-col-label">Lens Width</div>
              <div class="sg-breakdown-col-val">
                {{ r(animatedVals.lensW) }}
              </div>
              <div class="sg-breakdown-col-desc">
                Horizontal width of one lens.
              </div>
            </div>
            <div class="sg-breakdown-col">
              <div class="sg-breakdown-col-label">Bridge Width</div>
              <div class="sg-breakdown-col-val">
                {{ r(animatedVals.bridge) }}
              </div>
              <div class="sg-breakdown-col-desc">
                Width of the bridge between lenses.
              </div>
            </div>
            <div class="sg-breakdown-col">
              <div class="sg-breakdown-col-label">Temple Length</div>
              <div class="sg-breakdown-col-val">
                {{ r(animatedVals.temple) }}
              </div>
              <div class="sg-breakdown-col-desc">
                Length of the temple from hinge to tip.
              </div>
            </div>
          </div>
        </div>

        <!-- CTA strip -->
        <div class="sg-cta reveal">
          <div>
            <div class="sg-cta-title">Not sure about your size?</div>
            <div class="sg-cta-text">
              Check the inside of any frames you own — the size is stamped on
              the temple arm. Or email us and we'll advise before you order.
            </div>
          </div>
          <div>
            <a href="mailto:info@hooliganseyewear.com" class="sg-cta-btn">
              Email us
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import "./size_guide.css";

import GlassesFront from "~/components/size-guide/GlassesFront.vue";
import GlassesSide from "~/components/size-guide/GlassesSide.vue";
import { useStaticPageSeo } from "~/core/composables/useStaticPageSeo";

const SIZES = {
  XS: {
    lensW: 46,
    lensH: 33,
    bridge: 14,
    temple: 130,
    desc: "Narrow · Petite",
    best: "Narrow or petite face shapes",
  },
  S: {
    lensW: 50,
    lensH: 36,
    bridge: 16,
    temple: 137,
    desc: "Slim · Compact",
    best: "Slim to average face shapes",
  },
  M: {
    lensW: 53,
    lensH: 39,
    bridge: 18,
    temple: 142,
    desc: "Average · Universal",
    best: "Average face width — most common",
  },
  L: {
    lensW: 57,
    lensH: 41,
    bridge: 20,
    temple: 145,
    desc: "Wide · Bold",
    best: "Wider faces, bolder proportions",
  },
  XL: {
    lensW: 61,
    lensH: 44,
    bridge: 22,
    temple: 149,
    desc: "Oversized · Statement",
    best: "Wide faces, oversized fashion styles",
  },
};

export default {
  name: "SizeGuide",
  setup() {
    return useStaticPageSeo({
      title: "Eyeglasses & Sunglasses Size Guide | Hooligans Eyewear",
      heading: "Frame Measurements",
      description:
        "Use the Hooligans Eyewear size guide to understand frame, lens, bridge, and temple measurements and choose eyewear that fits comfortably.",
    });
  },
  components: {
    GlassesFront,
    GlassesSide,
  },
  data() {
    return {
      size: "M",
      hovered: null,
      animatedVals: { ...SIZES.M },
    };
  },
  created() {
    this.animRaf = null;
  },
  mounted() {
    this._reduceMotion =
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    this._setupRevealObserver();
  },
  computed: {
    sizeKeys() {
      return Object.keys(SIZES);
    },
    currentSize() {
      return SIZES[this.size];
    },
    targetVals() {
      return SIZES[this.size];
    },
  },
  watch: {
    size() {
      this.animateToTarget();
    },
  },
  beforeUnmount() {
    if (this.animRaf) cancelAnimationFrame(this.animRaf);
    if (this._revealObserver) {
      this._revealObserver.disconnect();
      this._revealObserver = null;
    }
  },
  methods: {
    _setupRevealObserver() {
      const els = this.$el.querySelectorAll(".reveal");
      if (!els.length) return;

      if (this._reduceMotion || typeof IntersectionObserver === "undefined") {
        els.forEach((el) => el.classList.add("visible"));
        return;
      }

      this._revealObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add("visible");
              this._revealObserver.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.12, rootMargin: "0px 0px -8% 0px" }
      );
      els.forEach((el) => this._revealObserver.observe(el));
    },
    r(v) {
      return Math.round(v);
    },
    animateToTarget() {
      if (this.animRaf) cancelAnimationFrame(this.animRaf);

      const from = { ...this.animatedVals };
      const to = { ...this.targetVals };
      const t0 = performance.now();
      const DUR = 750;

      const tick = (now) => {
        const t = Math.min((now - t0) / DUR, 1);
        // Cubic ease in-out
        const e = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
        const next = {};
        for (const k of Object.keys(to)) {
          if (typeof to[k] === "number") {
            next[k] = from[k] + (to[k] - from[k]) * e;
          } else {
            next[k] = to[k];
          }
        }
        this.animatedVals = next;
        if (t < 1) {
          this.animRaf = requestAnimationFrame(tick);
        }
      };

      this.animRaf = requestAnimationFrame(tick);
    },
  },
};
</script>
